/**
 * One shell-level Sidekick host.
 *
 * The panel deliberately does not live in Configure: keeping it beside the
 * persistent shell lets an open Bot X conversation remain mounted over Bot Y.
 * Its scope is locked while expanded. Closing releases that lock, so the next
 * open adopts whichever bot/workspace is currently underneath it.
 */
import React, {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthnMe } from '../../AuthnMeProvider';
import type { BotConfig, VoicePreset } from '../../config/types';
import type { HelperFieldSchema } from '../../utils/helperSchema';
import { getCustomerIdFromPath } from '../../utils/urlUtils';
import type { VoiceOption } from '../components/sidekickChatMessages';
import type { SidekickInitialPromptApply } from '../components/SidekickHelper';
import { buildHelperSchema, CLEAR_FIELD, coerceFieldValue } from '../components/sidekickFields';
import { planVoiceChange, type VoiceDraft } from '../components/sidekickVoiceChange';
import { useBotConfigForSidekick } from '../data/BotConfigContext';
import { useNavBots } from '../nav/useNavBots';
import {
  sidekickScopeAfterNavigation,
  type GlobalSidekickScope,
} from './globalSidekickScope';
import { buildWorkspaceSidekickContext } from './workspaceSidekickContext';
import HebrewSetupDialog from '../components/HebrewSetupDialog';
import { HEBREW_SETUP_SIDEKICK_CONTEXT, useHebrewSetupSuggestion } from '../hooks/useHebrewSetupSuggestion';

const SidekickHelper = lazy(() => import('../components/SidekickHelper'));
export type { GlobalSidekickScope } from './globalSidekickScope';

/**
 * Extras a launcher can hand the panel alongside the opening prompt. Used by
 * the Deepdub playground handoff: the user already chose a voice and a
 * language there, and the Sidekick should know the story so far without the
 * user retelling it.
 */
export interface GlobalSidekickLaunchOptions {
  /** Appended to the Sidekick's system-prompt context for this session. */
  context?: string;
  /** Applied to the bot draft with the prompt and shown as a "Set Voice" change card (bot scope only). */
  voice?: VoiceOption;
  /** How that change card names the voice; defaults to `voice.name`. */
  voiceLabel?: string;
  /** Written to `default_locale` with the prompt and shown as a change card (bot scope only). */
  locale?: string;
}

interface GlobalSidekickContextValue {
  launchPrompt: (scope: GlobalSidekickScope, prompt: string, options?: GlobalSidekickLaunchOptions) => void;
}

const GlobalSidekickContext = createContext<GlobalSidekickContextValue | null>(null);

export function useGlobalSidekick(): GlobalSidekickContextValue {
  const value = useContext(GlobalSidekickContext);
  if (!value) throw new Error('useGlobalSidekick must be used inside GlobalSidekickProvider');
  return value;
}

function botIdFromPath(pathname: string): string {
  const match = pathname.match(/^\/b\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function sameScope(a: GlobalSidekickScope | null, b: GlobalSidekickScope | null): boolean {
  if (!a || !b || a.kind !== b.kind) return a === b;
  return a.kind === 'bot'
    ? a.botId === (b as Extract<GlobalSidekickScope, { kind: 'bot' }>).botId
    : (a.customerId || 'self') === ((b as Extract<GlobalSidekickScope, { kind: 'workspace' }>).customerId || 'self');
}

let promptSequence = 0;

// Voice catalog, fetched once on demand. Only used to name the voice a
// "Set Voice" change card replaces; a miss just reads "Previous voice".
let voicePresetsCache: VoicePreset[] | null = null;
let voicePresetsRequest: Promise<void> | null = null;
function loadVoicePresets() {
  if (voicePresetsCache || voicePresetsRequest) return voicePresetsRequest;
  voicePresetsRequest = fetch('/api/voice-presets')
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => { voicePresetsCache = Array.isArray(data?.presets) ? data.presets : null; })
    .catch(() => {})
    .finally(() => { voicePresetsRequest = null; });
  return voicePresetsRequest;
}

export function GlobalSidekickProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const authn = useAuthnMe();
  const pathBotId = botIdFromPath(location.pathname);
  const pathCustomerId = getCustomerIdFromPath() || undefined;
  const nav = useNavBots(pathBotId, pathCustomerId);

  const routeScope = useMemo<GlobalSidekickScope | null>(() => {
    if (pathBotId) {
      return {
        kind: 'bot',
        botId: pathBotId,
        name: nav.bots.find((bot) => bot.bot_id === pathBotId)?.bot_name,
      };
    }
    if (/^\/customer(?:\/|$)/.test(location.pathname)) {
      return {
        kind: 'workspace',
        customerId: pathCustomerId || nav.customerId,
        name: nav.customerName || undefined,
      };
    }
    return null;
  }, [pathBotId, pathCustomerId, location.pathname, nav.bots, nav.customerId, nav.customerName]);

  const [activeScope, setActiveScope] = useState<GlobalSidekickScope | null>(routeScope);
  const [expanded, setExpanded] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState<
    { id: string; text: string; apply?: SidekickInitialPromptApply } | undefined
  >();
  // Extra prompt context from the last launchPrompt. It lives as long as the
  // panel that launch opened: closing it drops it, so the next open starts clean.
  const [launchExtras, setLaunchExtras] = useState<{ id: string; context: string } | null>(null);

  // Route changes only retarget a CLOSED panel. An expanded panel owns its
  // scope until the user minimizes it.
  useEffect(() => {
    setActiveScope((current) => {
      const next = sidekickScopeAfterNavigation(current, routeScope, expanded);
      if (sameScope(current, next) && current?.name === next?.name) return current;
      return next;
    });
    if (expanded) return;
    setInitialPrompt(undefined);
    setLaunchExtras(null);
  }, [expanded, routeScope]);

  const launchPrompt = useCallback((scope: GlobalSidekickScope, prompt: string, options?: GlobalSidekickLaunchOptions) => {
    const text = prompt.trim();
    if (!text) return;
    setActiveScope(scope);
    promptSequence += 1;
    const id = `${Date.now()}-${promptSequence}`;
    // Draft writes only make sense against a bot; a workspace panel has no draft.
    const apply: SidekickInitialPromptApply | undefined = scope.kind === 'bot' && (options?.voice || options?.locale)
      ? { voice: options?.voice, voiceLabel: options?.voiceLabel, locale: options?.locale }
      : undefined;
    // The change card names the voice being replaced; that needs the catalog.
    if (apply?.voice) void loadVoicePresets();
    setInitialPrompt({ id, text, apply });
    setLaunchExtras(options?.context?.trim() ? { id, context: options.context } : null);
    setExpanded(true);
  }, []);

  const handleExpandedChange = useCallback((next: boolean) => {
    setExpanded(next);
    if (next) return;
    setInitialPrompt(undefined);
    setLaunchExtras(null);
    setActiveScope(routeScope);
  }, [routeScope]);

  const handleInitialPromptHandled = useCallback((id: string) => {
    setInitialPrompt((current) => current?.id === id ? undefined : current);
  }, []);

  const botId = activeScope?.kind === 'bot' ? activeScope.botId : undefined;
  const ctl = useBotConfigForSidekick(botId, expanded || Boolean(initialPrompt));
  const hebrewSuggestion = useHebrewSetupSuggestion(botId || '');
  const roles = authn.user?.roles ?? [];
  const isPlatformAdmin = roles.includes('owner') || roles.includes('admin');

  const launchContext = launchExtras?.context?.trim() || '';
  const schema = useMemo<HelperFieldSchema>(() => {
    const withLaunchContext = (context: string) => (launchContext ? `${context}\n\n${launchContext}` : context);
    if (activeScope?.kind === 'workspace') {
      return {
        fields: [],
        context: withLaunchContext(buildWorkspaceSidekickContext({
          customerName: activeScope.name || nav.customerName,
          bots: nav.bots,
        })),
      };
    }
    if (!ctl) return { fields: [] };
    const configSchema = buildHelperSchema(ctl.config, {
      isAdmin: isPlatformAdmin,
      context: withLaunchContext(`You are a configuration assistant for a voice AI bot. Use the available tools to make the requested changes in this bot draft.
${HEBREW_SETUP_SIDEKICK_CONTEXT}
${hebrewSuggestion.suppressed ? 'The user already applied Hebrew template settings or chose not to see suggestions for this bot. Do not recommend or offer the Hebrew template again. They can still open it themselves from Templates.' : hebrewSuggestion.offered ? 'The Hebrew template review has already been offered in this conversation.' : ''}`),
    });
    // This schema-only review action is consumed below; BotConfig fields keep
    // coming exclusively from the shared field registry.
    return { ...configSchema, fields: [...configSchema.fields, hebrewSuggestion.field] };
  }, [activeScope, ctl, isPlatformAdmin, nav.bots, nav.customerName, hebrewSuggestion.offered, hebrewSuggestion.suppressed, hebrewSuggestion.field, launchContext]);

  const handleFieldUpdate = useCallback((fieldId: string, value: string | number | null) => {
    if (!ctl) return;
    if (hebrewSuggestion.handleFieldUpdate(fieldId, value)) return;
    const next = coerceFieldValue(fieldId, value);
    if (next === undefined) return;
    ctl.update(
      fieldId as keyof BotConfig,
      (next === CLEAR_FIELD ? undefined : next) as never,
    );
  }, [ctl, hebrewSuggestion.handleFieldUpdate]);

  const writeVoiceDraft = useCallback((draft: VoiceDraft) => {
    if (!ctl) return;
    ctl.update('personas', draft.personas as never);
    ctl.update('emotions_yaml', draft.emotions_yaml as never);
    ctl.update('voice_gender', draft.voice_gender as never);
  }, [ctl]);

  // Returns the before/after snapshots so the panel can show the write as an
  // undoable change card.
  const handleVoiceSelect = useCallback((voice: VoiceOption) => {
    if (!ctl) return;
    const change = planVoiceChange(ctl.config, voice, voicePresetsCache);
    writeVoiceDraft(change.after);
    return change;
  }, [ctl, writeVoiceDraft]);

  const value = useMemo(() => ({ launchPrompt }), [launchPrompt]);
  const activeBotName = activeScope?.kind === 'bot'
    ? String(ctl?.config.bot_name || activeScope.name || activeScope.botId)
    : '';
  const title = activeScope?.kind === 'bot'
    ? `Sidekick · ${activeBotName}`
    : `Sidekick · ${activeScope?.name || 'Workspace'}`;
  const onActiveBotConfigPage = Boolean(
    botId
    && pathBotId === botId
    && /^\/b\/[^/]+\/config\/?$/.test(location.pathname),
  );
  const helperReady = (activeScope?.kind === 'workspace' && (!expanded || !nav.loading))
    || (activeScope?.kind === 'bot' && ctl && (!expanded || !ctl.loading));

  return (
    <GlobalSidekickContext.Provider value={value}>
      {children}
      {authn.isAuthenticated && activeScope && helperReady && (
        <Suspense fallback={null}>
          <SidekickHelper
            key={activeScope.kind === 'bot' ? `bot:${activeScope.botId}` : `workspace:${activeScope.customerId || 'self'}`}
            schema={schema}
            position="bottom-right"
            targetBotId={botId}
            scopeMode={activeScope.kind === 'bot' ? 'bot' : 'workspace'}
            chatScope={activeScope.kind === 'bot'
              ? { kind: 'bot', id: activeScope.botId }
              : { kind: 'customer', id: activeScope.customerId }}
            title={title}
            expanded={expanded}
            onExpandedChange={handleExpandedChange}
            initialPrompt={initialPrompt}
            onInitialPromptHandled={handleInitialPromptHandled}
            enableSpark={onActiveBotConfigPage}
            dirty={ctl?.dirty ?? false}
            saving={ctl?.saving ?? false}
            onPublish={ctl ? ctl.save : undefined}
            onFieldUpdate={ctl ? handleFieldUpdate : undefined}
            onVoiceSelect={ctl ? handleVoiceSelect : undefined}
            onVoiceRestore={ctl ? writeVoiceDraft : undefined}
            hebrewSetupSuggested={Boolean(ctl && hebrewSuggestion.offered)}
            onReviewHebrewSetup={ctl ? hebrewSuggestion.openReview : undefined}
            onClearHebrewSetupSuggestion={ctl ? hebrewSuggestion.clearOffer : undefined}
          />
        </Suspense>
      )}
      {botId && ctl && (
        <HebrewSetupDialog
          key={botId}
          botId={botId}
          open={hebrewSuggestion.open}
          onOpenChange={hebrewSuggestion.onOpenChange}
          onApply={ctl.updateMany}
          config={ctl.config}
          source="sidekick"
        />
      )}
    </GlobalSidekickContext.Provider>
  );
}
